import { DOWN_ARROW } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import isEqual from 'fast-deep-equal';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  Observable,
  observeOn,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { ProjectRoleName } from '@blaze-shared/api-contracts-domain';
import { appStateActions, projectContextSelectors } from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import { hasProjectRole } from '@blaze-shared/auth-domain';
import {
  getBackground,
  InputElementMetaUiFeatures,
  offsetBorderRadius,
} from '@blaze-shared/elements-domain';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';
import { compareAscBy } from '@blaze-shared/platform-util';
import {
  convertBlazeSchemaToElementSchema,
  DatasetRow,
  selectItemByValue,
  VarAccount,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  isDeepEqual,
  isDefined,
  useEmitTracker,
  ValueWithUnitOfMeasure,
} from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent, ColumnIdOrName } from '../__base/base.component';

type AccountOption = {
  label: string;
  value: DatasetRow;
};

@UntilDestroy()
@Component({
  selector: 'be-accounts-dropdown',
  templateUrl: './accounts-dropdown.component.html',
  styleUrls: ['./accounts-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountsDropdownComponent
  extends CanvasElementBaseComponent<'accountsDropdown'>
  implements CanvasElementComponent<'accountsDropdown'>, AfterViewInit, OnChanges, OnInit, OnDestroy
{
  private authService = inject(AuthService);
  private localStorageService = inject(LocalStorageService);
  private variablesFacade = inject(VariablesFacade);

  @Input() selected?: AccountOption;

  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild('inputEl') inputEl!: ElementRef<HTMLDivElement>;
  @ViewChild(MatMenu) menuContainer!: MatMenu;
  @ViewChildren(MatMenuTrigger) menuTriggerList!: QueryList<MatMenuTrigger>;

  private outputEmitTracker = useEmitTracker();
  private dataSelected = this.getDataInput('selected');
  private wasEmitted = false;

  optionsList$$: BehaviorSubject<AccountOption[]> = new BehaviorSubject<AccountOption[]>([]);
  trackAccountOptions = (i: number, option: AccountOption): string => option.value._id;
  displayedOptions$!: Observable<AccountOption[]>;

  readonly elementKeyRoot = 'b-ad-dd';
  readonly elementSizeKeyRoot = 'b-ad-s';
  readonly ProjectRole = ProjectRoleName;

  elementKey!: string;
  elementSizeKey!: string;

  filterField$$: BehaviorSubject<string> = new BehaviorSubject('');

  labelEnabled!: boolean;
  @ColumnIdOrName() labelField = 'label';
  labelText!: string;
  @ColumnIdOrName() valueField = 'value';

  accentColor!: string;
  dropdownOpened = false;
  fontSize!: ValueWithUnitOfMeasure | undefined;
  fontFamily!: string;
  // 2* 8px padding
  inputPaddings = 16;
  inputWrapperStyles!: Record<string, string>;
  projectId = '';
  styles!: Record<string, string>;
  get menuTrigger(): MatMenuTrigger | undefined {
    return this.menuTriggerList.get(0);
  }

  get isVisible$(): Observable<boolean> {
    return combineLatest({
      varUser: this.projectContextFacade.varUser$,
      userInfo: this.projectContextFacade.userInfo$,
      projectId: this.store.select(runtimeSelectors.selectProjectId),
    }).pipe(
      filter((projectId) => !!projectId),
      map(({ projectId }) => {
        return this.userHasAccess(projectId) && !this.projectContextFacade.isPreviewOfAnotherUser;
      }),
    );
  }

  get isMenuOpen(): boolean {
    return !!this.menuTrigger?.menuOpen;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }
      this.labelField = 'label';
      this.valueField = 'value';

      const { title } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;

      this.updateStyles();
    }

    if (changes?.['selected'] && this.optionsList$$.value.length) {
      this.updateOutput(
        selectItemByValue(this.optionsList$$.value, this.selected, this.valueField),
      );
    }
  }
  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        safeDetectChanges(this.cdr);
      });

    this.stylesCleanup.register([this.elementKey, this.elementSizeKey]);
    if (this.bindingResults?.['selected']) {
      this.updateOutput(
        selectItemByValue(this.optionsList$$.value, this.selected, this.valueField),
      );
    }

    // Fetch $Accounts for the given project.
    combineLatest({
      accounts: this.store.select(projectContextSelectors.selectExternalAccounts),
      isVisible: this.isVisible$,
    })
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((curr, prev) => isEqual(curr, prev)),
        concatLatestFrom(() => [
          this.projectContextFacade.varAccount$,
          this.store.select(runtimeSelectors.selectProjectId),
        ]),
        filter(([_, _currSetAccount, projectId]) => !!projectId),
        tap(([_, _currSetAccount, projectId]) => (this.projectId = projectId)),
        mergeMap(([{ accounts, isVisible }, currSetAccount, projectId]) => {
          return this.authService.refreshProjectRoles(projectId).pipe(
            map(() => {
              return { accounts, currSetAccount, projectId, isVisible };
            }),
          );
        }),
      )
      .subscribe(({ accounts, currSetAccount, isVisible }) => {
        accounts = accounts || [];
        if (isVisible) {
          const optionsList = accounts
            .map((account: DatasetRow) => ({ value: { ...account }, label: account?.['Name'] }))
            .sort(compareAscBy('label'));
          const lastSelectedAccount = this.localStorageService.getForProject<VarAccount>(
            this.projectId,
            StorageKey.LastExternalAccount,
          );
          const preselectAccount = currSetAccount?.Name ? currSetAccount : lastSelectedAccount;
          const preselect = optionsList.find(
            ({ value }) => value?.['_id'] === preselectAccount?._id,
          );
          this.selected = preselect ?? optionsList[0];
          this.optionsList$$.next(optionsList);

          if (this.schemaService.accountsSchema) {
            const eventSchema = convertBlazeSchemaToElementSchema(
              this.schemaService.accountsSchema,
              this.elementId,
              'change',
            );
            this.setDefaultEventSchema('change', eventSchema);
          } else {
            this.setDefaultEventSchema('change');
          }

          if ((this.selected && !preselect) || (this.selected && !currSetAccount?.Name)) {
            this.onSelect(this.selected);
          } else {
            this.setFilterInputValue();
          }
          safeDetectChanges(this.cdr);
        }
      });

    // Update schema when Accounts table schema changes
    this.projectContextFacade.varAccount$
      .pipe(
        switchMap(() => this.schemaService.obtainAccountsSchema()),
        distinctUntilChanged(isDeepEqual),
        filter(isDefined),
        map((accountsSchema) =>
          convertBlazeSchemaToElementSchema(accountsSchema, this.elementId, 'change'),
        ),
        untilDestroyed(this),
      )
      .subscribe((eventSchema) => {
        this.setDefaultEventSchema('change', eventSchema);
      });

    this.displayedOptions$ = combineLatest({
      filter: this.filterField$$,
      optionsList: this.optionsList$$,
    }).pipe(
      map(({ filter, optionsList }) =>
        optionsList.filter(({ label }) => label.toString().toLowerCase().indexOf(filter) > -1),
      ),
      untilDestroyed(this),
    );
  }

  ngAfterViewInit(): void {
    this.menuTriggerList.changes
      .pipe(
        startWith(this.menuTriggerList),
        map((menuTriggerList: QueryList<MatMenuTrigger>) => menuTriggerList.get(0)),
        switchMap((menuTrigger) => (menuTrigger ? menuTrigger.menuOpened : of())),
        observeOn(asyncScheduler), // wait for the menu to appear on the page
        untilDestroyed(this),
      )
      .subscribe(() => {
        const input = this.filterInput.nativeElement;
        input.focus();
        input.select();
      });

    /**
     * Set selected value to input when closing dropdown
     */
    this.menuTriggerList.changes
      .pipe(
        startWith(this.menuTriggerList),
        map((menuTriggerList: QueryList<MatMenuTrigger>) => menuTriggerList.get(0)),
        switchMap((menuTrigger) => (menuTrigger ? menuTrigger.menuClosed : of())),
        untilDestroyed(this),
      )
      .subscribe(() => {
        // On menu close - turn off autocomplete and select value
        this.setFilterInputValue();

        window.getSelection()?.removeAllRanges();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose([this.elementKey, this.elementSizeKey]);
  }

  isSelected(option?: AccountOption): boolean {
    return (option as any)?.[this.valueField] === (this.selected as any)?.[this.valueField];
  }

  getItemLabel(option?: AccountOption): string {
    return (option as any)?.[this.labelField];
  }

  onResized(box: DOMRectReadOnly): void {
    this.stylesCleanup.update(
      `${this.elementSizeKey}`,
      `.${this.elementKey} { width: ${box.width + this.inputPaddings}px; }`,
    );
  }

  onSelect(item: AccountOption): void {
    this.store.dispatch(appStateActions.updateVarAccount({ varAccount: item.value as VarAccount }));
    this.localStorageService.setForProject(
      this.projectId,
      StorageKey.LastExternalAccount,
      item?.value,
    );
    const accountName = item?.value?.Name;
    this.variablesFacade.initVariables(this.projectId, accountName);
    this.updateOutput(item);
  }

  onInputContainerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (!this.isMenuOpen) {
      this.filterField$$.next('');
      this.menuTrigger?.openMenu();
    } else if (target !== this.filterInput?.nativeElement) {
      this.menuTrigger?.closeMenu();
    }
  }

  onInputKeyUp(event: KeyboardEvent): void {
    if (event.keyCode === DOWN_ARROW) {
      this.menuContainer.focusFirstItem();
      return;
    }

    this.filterOptions();
  }

  filterOptions(): void {
    const searchInput = this.filterInput.nativeElement;
    this.filterField$$.next(searchInput.value.toLowerCase() || '');
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, input, list } = style || {};
    if (!style || !title || !general || !input || !list) {
      return;
    }
    this.fontSize = style.fontSize;
    this.fontFamily = style.fontFamily || '';
    this.accentColor = general.accent.color1;

    const styles: Record<string, string> = {};

    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.color.color1,
    };

    if (title.position === 'top') {
      styles['flex-direction'] = `column`;
    } else {
      inputWrapperStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
    }

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
      this.inputPaddings = 18;
    }

    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
    }

    this.styles = styles;
    this.inputWrapperStyles = inputWrapperStyles;

    const accent10 = `${general.accent.color1.substring(0, 7)}0A`;

    this.stylesCleanup.update(
      `${this.elementKey}`,
      `
    .${this.elementKey}.mat-mdc-menu-panel {
      background: ${getBackground(list.background)};
      ${list.border.selectorType !== 'none' ? `border: 1px solid ${list.border.color1};` : ''}
      border-radius: ${input.radius};
    }
    .${this.elementKey} button.mat-mdc-menu-item {
      color: ${list.value.color1};
      font-size: ${style.fontSize};
      font-family: ${style.fontFamily};
      line-height: ${style.lineHeight};
      border-radius: ${offsetBorderRadius(list.radius, -4)};
    }
    .${this.elementKey} button:hover {
      color: ${general.accent.color1};
    }
    .${this.elementKey} .active {
      background: ${accent10};
      color: ${general.accent.color1};
    }`,
    );
  }

  private updateOutput(data: AccountOption): void {
    this.selected = data;
    this.setFilterInputValue();
    if (this.outputEmitTracker.shouldEmit(data)) {
      this.output.emit({
        data: data.value,
        eventType: 'change',
        isInitialData: !this.wasEmitted,
        schema: this.getEventSchema('change'),
      });
      this.wasEmitted = true;
    }
  }

  /**
   * Component should only be visible for users with ProjectEditor access level.
   */
  private userHasAccess(projectId?: string): boolean {
    const user = this.projectContextFacade.userInfo;
    if (projectId || this.projectId) {
      return hasProjectRole(user, ProjectRoleName.ProjectEditor, projectId || this.projectId);
    }
    return false;
  }

  private setFilterInputValue(): void {
    this.filterInput.nativeElement.value = this.getItemLabel(this.selected) ?? '';
  }
}
